// consent.js: the theme's own cookie consent (markup and texts in snippets/consent.liquid).
// Shopify's Customer Privacy API stores the choice (Shopify then loads or holds the web pixels); Google Consent Mode v2
// follows it (defaults "denied" come from the head part of the snippet). The banner shows only while Shopify holds no
// choice for this visitor (currentVisitorConsent() all "", measured 17.10.2026; shouldShowBanner() stays true after a
// choice, so it cannot tell). Brief and measurements: ../vipibax/reports/2026-10-17-consent/.
(() => {
  const box = document.querySelector('[data-consent]');
  if (!box) return;
  // never two banners: while the Pandectes embed is on (its settings script runs in <head>, before this deferred file)
  // our markup goes before it ever shows. Liquid cannot do this check: app embeds join the page after Liquid runs.
  if (window.pandectesBannerSettings || document.querySelector('pandectes-cmp')) { box.remove(); return; }
  const notice = box.querySelector('[data-consent-notice]');
  const dialog = box.querySelector('[data-consent-dialog]');
  const css = document.querySelector('[data-consent-css]');
  const switches = [...dialog.querySelectorAll('[data-consent-purpose]')];
  const PURPOSES = ['analytics', 'marketing', 'preferences'];
  const NONE = { analytics: false, marketing: false, preferences: false };
  const ALL = { analytics: true, marketing: true, preferences: true };
  const gtag = window.gtag || function gtag() { (window.dataLayer = window.dataLayer || []).push(arguments); };
  let current = null; // the stored choice, null while there is none
  let opener = null;

  // the stylesheet loads as "print" so it never blocks the page; nothing shows before it is in
  const cssReady = new Promise((resolve) => {
    const on = () => { css.media = 'all'; resolve(); };
    if (!css || css.sheet) { if (css) css.media = 'all'; resolve(); return; }
    css.addEventListener('load', on, { once: true });
    css.addEventListener('error', on, { once: true });
  });

  // 19.10.: Shopify's own scripts (trekkie, perf-kit, web pixels) also create Shopify.customerPrivacy, as a bare object with
  // serverTimingSupportVerified / cachedToken, on pages without server timing. Safari gets such pages after a redirect from
  // another host (ad click, myshopify.com) and after Shopify's shop.app sign-in bounce (?_su_rec=), and Shopify then does not
  // load the API there; a choice made on that page never reached Shopify (measured,
  // ../vipibax/reports/2026-10-19-neustart/tracking/BEFUND.md). Only the API itself counts; loadFeatures adds it to that object.
  const privacy = () => {
    const api = window.Shopify && window.Shopify.customerPrivacy;
    return api && typeof api.setTrackingConsent === 'function' && typeof api.currentVisitorConsent === 'function' ? api : null;
  };
  // Shopify.loadFeatures comes from a deferred script in content_for_header, which runs before this one; the wait is a
  // fallback. Without the API nothing can be stored, so nothing shows (no consent = Shopify and Google stay denied).
  const withApi = (done, tries = 0) => {
    if (privacy()) { done(privacy()); return; }
    if (!(window.Shopify && window.Shopify.loadFeatures)) {
      if (tries < 50) setTimeout(() => withApi(done, tries + 1), 100);
      return;
    }
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error && privacy()) done(privacy());
    });
  };

  const choiceOf = (api) => {
    const v = (api.currentVisitorConsent && api.currentVisitorConsent()) || {};
    if (!PURPOSES.some((p) => v[p] === 'yes' || v[p] === 'no')) return null;
    return { analytics: v.analytics === 'yes', marketing: v.marketing === 'yes', preferences: v.preferences === 'yes' };
  };

  const granted = (on) => (on ? 'granted' : 'denied');
  const publish = (c) => {
    window.__consent = c; // snippets/analytics-events reads it
    gtag('consent', 'update', {
      analytics_storage: granted(c.analytics),
      ad_storage: granted(c.marketing),
      ad_user_data: granted(c.marketing),
      ad_personalization: granted(c.marketing),
      functionality_storage: granted(c.preferences),
      personalization_storage: granted(c.preferences),
    });
    // after the update: GTM must see "granted" when its first event (gtm.js) runs, else consent-gated tags stay blocked
    document.dispatchEvent(new CustomEvent('consent:published', { detail: c })); // snippets/analytics-events loads GTM on analytics
  };

  // Placement: the rules of the Pandectes window in global.js (L22, placeCmp). Phones and short landscape screens get a
  // bar at the bottom or top edge, wider screens a card in a corner; the first place that covers none of the elements
  // below wins, else the one that covers least. The card and the bar ride above the product page's buy bar.
  const BAR = '(max-width: 620px), (max-height: 500px) and (min-width: 560px)';
  // 18.10.: also the VAT/shipping note, "Auf Lager" and the size choice (CLAUDE.md: never bury them); the centred card
  // then moves over the product photo on product pages
  const KEEP = 'main .base-price, main .biocide-warning, main [name="add"], main .buybox__taxnote, main .buybox__stock, main .variant-picker, main [data-variant-tiles], main .selling-plans';
  // 18.10. (plan point 45): on the contact page the centred card covered the message field (1280 x 800, measured); the
  // fields of the contact, B2B and account forms count in full
  const FIELDS = ['contact', 'account'].map((a) => `main form[action*="/${a}"] :is(input:not([type="hidden"]), textarea, button)`).join(', ');
  const inView = (el) => {
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.bottom > 0 && r.top < window.innerHeight ? r : null;
  };
  const place = () => {
    if (notice.hidden) return;
    const sticky = document.querySelector('[data-sticky-atc].is-visible');
    notice.style.setProperty('--consent-lift', `${sticky ? Math.round(sticky.getBoundingClientRect().height) : 0}px`);
    // 18.10. (plan point 45): on small screens no place leaves everything free (320 x 568, 568 x 320, and the first product
    // cards at 375 x 812 and 768 x 1024, measured). The price and the mandatory notes come first (CLAUDE.md: never hide
    // them); among the places that cover least of them, the page title counts double, the form fields in full, the first
    // button a quarter (it is needed only after the choice).
    const must = [document.querySelector('main .price, main [class*="__price"]'), ...document.querySelectorAll(KEEP)]
      .map(inView).filter(Boolean).map((r) => ({ r, w: 1 }));
    const rest = [...document.querySelectorAll(FIELDS)].map(inView).filter(Boolean).map((r) => ({ r, w: 1 }));
    const title = inView(document.querySelector('main h1'));
    const button = inView([...document.querySelectorAll('main a.button, main button.button')].find(inView));
    if (title) rest.push({ r: title, w: 2 });
    if (button) rest.push({ r: button, w: 0.25 });
    const area = (list, b) => list.reduce((sum, { r, w }) => sum + w * Math.max(0, Math.min(r.right, b.right) - Math.max(r.left, b.left))
      * Math.max(0, Math.min(r.bottom, b.bottom, window.innerHeight) - Math.max(r.top, b.top, 0)), 0);
    // 18.10.: wider screens try the centred card first, then the narrower corner cards
    const places = window.matchMedia(BAR).matches ? ['bottom', 'top'] : ['bottom-center', 'bottom-left', 'bottom-right', 'top-right', 'top-left'];
    let best = null;
    for (const p of places) {
      notice.dataset.place = p;
      const b = notice.getBoundingClientRect();
      const now = { p, must: area(must, b), rest: area(rest, b) };
      if (!best || now.must < best.must || (now.must === best.must && now.rest < best.rest)) best = now;
      if (now.must === 0 && now.rest === 0) break;
    }
    notice.dataset.place = best.p;
    // 18.10.: the page gets bottom space as tall as a bottom notice, so the footer (legal notice, privacy policy) can always
    // scroll above it; the notice itself no longer repeats those links. 19.10.: on <html>, because the product page's buy
    // bar (global.js updateSticky) clears the body padding on every scroll and the footer biocide note sat under the card
    const nb = notice.getBoundingClientRect();
    document.documentElement.style.paddingBottom = best.p.startsWith('bottom') ? `${Math.ceil(window.innerHeight - nb.top) + 8}px` : '';
  };
  let resizeTimer = 0;
  window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(place, 150); }, { passive: true });
  window.addEventListener('load', place);
  const stickyAtc = document.querySelector('[data-sticky-atc]');
  if (stickyAtc) new MutationObserver(() => requestAnimationFrame(place)).observe(stickyAtc, { attributes: true, attributeFilter: ['class'] });

  const showNotice = () => cssReady.then(() => {
    notice.hidden = false;
    place();
    setTimeout(place, 400); // again after the web fonts
  });
  const hideNotice = () => {
    const hadFocus = notice.contains(document.activeElement);
    notice.hidden = true;
    document.documentElement.style.paddingBottom = '';
    if (hadFocus) {
      const main = document.getElementById('MainContent');
      if (main) main.focus({ preventScroll: true });
    }
  };

  // A withdrawn category also loses the browser cookies its tags left on this domain (Shopify removes its own
  // _shopify_analytics / _shopify_marketing itself, measured). Server-set HttpOnly cookies (FPID) cannot be reached here.
  // 21.10.2026: __kla_id / __kle_id from snippets/klaviyo-onsite (Klaviyo, marketing)
  const LEFTOVERS = { analytics: /^(_ga|_ga_.+|_gid|FPLC)$/, marketing: /^(_gcl_.+|_fbp|_fbc|FPAU|FPGCL.+|__kla_id|__kle_id)$/ };
  // 21.10.2026: Klaviyo's script also keeps its form state in localStorage (klaviyoOnsite, measured on the work theme)
  const LEFTOVER_STORAGE = { marketing: /^klaviyoOnsite$/ };
  const clearWithdrawn = (c) => {
    const host = window.location.hostname.replace(/^www\./, '');
    document.cookie.split(';').map((p) => p.split('=')[0].trim()).forEach((name) => {
      const purpose = Object.keys(LEFTOVERS).find((p) => !c[p] && LEFTOVERS[p].test(name));
      if (!purpose) return;
      [`; domain=.${host}`, ''].forEach((domain) => { document.cookie = `${name}=; Max-Age=0; path=/${domain}`; });
    });
    try {
      Object.keys(localStorage).forEach((key) => {
        if (Object.keys(LEFTOVER_STORAGE).some((p) => !c[p] && LEFTOVER_STORAGE[p].test(key))) localStorage.removeItem(key);
      });
    } catch (error) { /* storage blocked: nothing was stored there either */ }
  };

  // 18.10. (plan point 45): Shopify stores the choice with a request; a page already on its way does not know it yet and
  // showed the notice again (measured: a page change 0 or 30 ms after the click, 150 ms held). The choice waits in this
  // tab's sessionStorage until a page load reads it back from Shopify; until then every page sends it again.
  const PENDING = 'consent-pending';
  const pending = (c) => {
    try {
      if (c === undefined) return JSON.parse(sessionStorage.getItem(PENDING));
      if (c) sessionStorage.setItem(PENDING, JSON.stringify(c));
      else sessionStorage.removeItem(PENDING);
    } catch (error) { /* storage blocked: Shopify's request alone */ }
    return null;
  };

  const save = (c) => {
    current = c;
    pending(c);
    publish(c);
    clearWithdrawn(c);
    hideNotice();
    if (dialog.open) dialog.close();
    // sale_of_data follows marketing, except under Global Privacy Control (Shopify keeps it "no" then, measured). It
    // must be sent on "yes" too: after an earlier "no" it stays "no" (measured), and the Meta and TAGGRS pixels list
    // sale of data among their purposes (webPixelsConfigList), so Shopify could keep holding them.
    const gpc = navigator.globalPrivacyControl === true;
    const choice = { analytics: c.analytics, marketing: c.marketing, preferences: c.preferences, sale_of_data: c.marketing && !gpc };
    withApi((api) => api.setTrackingConsent(choice, (result) => {
      if (!(result && result.error)) return;
      // not stored: say so on the page (data attribute for checks) and ask again instead of failing silently
      box.dataset.consentError = String(result.error).slice(0, 120);
      current = null;
      showNotice();
    }));
  };

  // the stored choice is known once Shopify's API has answered; the dialog waits for it, so a returning visitor
  // never sees (and saves) all switches off by mistake
  let apiReady = null;
  const stateReady = new Promise((resolve) => { apiReady = resolve; });
  const openDialog = (from) => {
    opener = from || document.activeElement;
    Promise.all([cssReady, stateReady]).then(() => {
      const c = current || NONE; // nothing is ticked before a choice
      switches.forEach((s) => { s.checked = !!c[s.dataset.consentPurpose]; });
      if (!dialog.open) dialog.showModal();
    });
  };
  dialog.addEventListener('close', () => {
    // back to the button that opened it; after a choice from the notice that button is gone, so to the page
    const target = opener && opener.isConnected && !opener.closest('[hidden]') ? opener : document.getElementById('MainContent');
    if (target) target.focus({ preventScroll: true });
  });
  // keep Tab inside the open dialog (the page behind is inert already; this stops the jump to the browser bar)
  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const items = [...dialog.querySelectorAll('button, a[href], input:not([disabled])')].filter((el) => el.getClientRects().length);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });

  box.addEventListener('click', (event) => {
    const btn = event.target.closest('button');
    if (!btn) return;
    if (btn.hasAttribute('data-consent-accept')) save(ALL);
    else if (btn.hasAttribute('data-consent-reject')) save(NONE);
    else if (btn.hasAttribute('data-consent-save')) save(Object.fromEntries(switches.map((s) => [s.dataset.consentPurpose, s.checked])));
    else if (btn.hasAttribute('data-consent-open')) openDialog(btn);
    else if (btn.hasAttribute('data-consent-close')) dialog.close();
  });
  // footer "Cookie-Einstellungen" and any other [data-cookie-settings]: reopen and change the choice either way
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cookie-settings]');
    if (!link) return;
    event.preventDefault();
    openDialog(link);
  });

  withApi((api) => {
    apiReady();
    if (current) return; // a choice made in this page already (save sets it)
    const waiting = pending();
    const stored = choiceOf(api);
    if (waiting && PURPOSES.every((p) => typeof waiting[p] === 'boolean')
      && !(stored && PURPOSES.every((p) => stored[p] === waiting[p]))) { save(waiting); return; }
    pending(null);
    current = stored;
    if (current) publish(current);
    else {
      window.__consent = NONE;
      showNotice();
    }
  });
})();
